import { useEffect, useState } from "react";
import { adminGetPremiumConfig, adminSavePremiumConfig, adminPremiumUsers, adminPremiumGrant, adminPremiumRevoke } from "@/lib/api";
import { Crown, Loader2, Save, Check, AlertCircle, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const WEBHOOK_PATH = "/api/premium/razorpay/webhook";

export default function AdminPremium() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ premium_price: 99, premium_plan_id: "", premium_family_price: 499, premium_family_plan_id: "", premium_enabled: false, premium_rzp_key_id: "", premium_rzp_key_secret: "", premium_webhook_secret: "" });
  const [busy, setBusy] = useState(false);

  const load = () => adminGetPremiumConfig().then((d) => { setCfg(d); setForm((f) => ({ ...f, premium_price: d.premium_price, premium_plan_id: d.premium_plan_id, premium_family_price: d.premium_family_price, premium_family_plan_id: d.premium_family_plan_id, premium_enabled: d.premium_enabled, premium_rzp_key_id: d.premium_rzp_key_id })); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { premium_price: form.premium_price, premium_plan_id: form.premium_plan_id, premium_family_price: form.premium_family_price, premium_family_plan_id: form.premium_family_plan_id, premium_enabled: form.premium_enabled, premium_rzp_key_id: form.premium_rzp_key_id };
      if (form.premium_rzp_key_secret) payload.premium_rzp_key_secret = form.premium_rzp_key_secret;
      if (form.premium_webhook_secret) payload.premium_webhook_secret = form.premium_webhook_secret;
      const d = await adminSavePremiumConfig(payload);
      setCfg(d); setForm((f) => ({ ...f, premium_rzp_key_secret: "", premium_webhook_secret: "" }));
      toast.success("Premium settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}${WEBHOOK_PATH}`;

  return (
    <div className="max-w-2xl" data-testid="admin-premium">
      <div className="mb-5 flex items-center gap-3">
        <Crown className="h-6 w-6 text-saffron" />
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">Panchangam Premium</h1>
          <p className="text-sm text-ink-3">₹/month Razorpay subscription. Create a Subscription <b>Plan</b> in your Razorpay dashboard and paste its Plan ID below.</p>
        </div>
      </div>

      <div className="space-y-5 border border-line bg-surface p-6">
        <label className="flex items-center gap-2.5">
          <input type="checkbox" checked={form.premium_enabled} onChange={(e) => setForm({ ...form, premium_enabled: e.target.checked })} data-testid="premium-enabled" className="h-4 w-4 accent-[var(--saffron)]" />
          <span className="text-sm text-ink-2">Premium <b>enabled</b> (shows the Unlock button to users)</span>
        </label>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Price (₹ / month)</label>
          <input className={inp} type="number" min="1" value={form.premium_price} onChange={(e) => setForm({ ...form, premium_price: e.target.value })} data-testid="premium-price-input" />
          <p className="mt-1 text-xs text-ink-3">Display price. The actual billed amount is set by your Razorpay Plan — keep them matching.</p>
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Razorpay Plan ID</label>
          <input className={inp} placeholder="plan_XXXXXXXXXXXX" value={form.premium_plan_id} onChange={(e) => setForm({ ...form, premium_plan_id: e.target.value })} data-testid="premium-plan-id" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 rounded-lg border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.05)] p-4">
          <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-saffron">Family Plan (up to 5 members + 1 free consultation)</div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Family price (₹ / month)</label>
            <input className={inp} type="number" min="1" value={form.premium_family_price} onChange={(e) => setForm({ ...form, premium_family_price: e.target.value })} data-testid="premium-family-price-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Family Razorpay Plan ID</label>
            <input className={inp} placeholder="plan_XXXXXXXXXXXX" value={form.premium_family_plan_id} onChange={(e) => setForm({ ...form, premium_family_plan_id: e.target.value })} data-testid="premium-family-plan-id" />
          </div>
        </div>

        <div className="border-t border-line pt-4">
          <p className="mb-2 text-sm font-medium text-ink">Razorpay keys <span className="font-normal text-ink-3">(leave blank to reuse your donation keys)</span></p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={inp} placeholder="Key ID (rzp_...)" value={form.premium_rzp_key_id} onChange={(e) => setForm({ ...form, premium_rzp_key_id: e.target.value })} data-testid="premium-key-id" />
            <input className={inp} type="password" placeholder={cfg?.premium_secret_set ? "Key Secret (set — leave blank to keep)" : "Key Secret"} value={form.premium_rzp_key_secret} onChange={(e) => setForm({ ...form, premium_rzp_key_secret: e.target.value })} data-testid="premium-key-secret" />
          </div>
          {cfg && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-3">
              {(cfg.premium_secret_set || cfg.fallback_keys) ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-600" />}
              {cfg.premium_secret_set ? "Dedicated premium keys set." : cfg.fallback_keys ? "No premium keys — will use donation keys." : "No keys configured yet."}
            </p>
          )}
        </div>

        <div className="border-t border-line pt-4">
          <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Webhook Secret</label>
          <input className={inp} type="password" placeholder={cfg?.premium_webhook_set ? "Set — leave blank to keep" : "Paste after creating the webhook"} value={form.premium_webhook_secret} onChange={(e) => setForm({ ...form, premium_webhook_secret: e.target.value })} data-testid="premium-webhook-secret" />
          <p className="mt-2 text-xs text-ink-3">In Razorpay → Settings → Webhooks, add this URL and subscribe to subscription events (activated, charged, halted, cancelled) + payment.failed:</p>
          <code className="mt-1 block break-all rounded bg-parchment px-2 py-1 text-xs text-ink-2" data-testid="premium-webhook-url">{webhookUrl}</code>
          {cfg?.premium_webhook_set && <p className="mt-1 flex items-center gap-1.5 text-xs text-emerald-700"><Check className="h-3.5 w-3.5" /> Webhook secret set.</p>}
        </div>

        <button onClick={save} disabled={busy} data-testid="premium-config-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>

      <GrantAccess />
    </div>
  );
}

function GrantAccess() {
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [tier, setTier] = useState("single");
  const [extend, setExtend] = useState(true);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => adminPremiumUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);

  const grant = async () => {
    if (!email.trim()) { toast.error("Enter a user email"); return; }
    setBusy(true);
    try { const r = await adminPremiumGrant({ email: email.trim(), days: Number(days), tier, extend }); toast.success(`Granted ${r.tier === "family" ? "Family" : "Single"} to ${r.email} until ${String(r.current_end).slice(0, 10)}`); setEmail(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not grant"); } finally { setBusy(false); }
  };
  const revoke = async (em) => {
    if (!window.confirm(`Revoke premium for ${em}?`)) return;
    try { await adminPremiumRevoke(em); toast.success("Revoked"); load(); } catch (e) { toast.error("Could not revoke"); }
  };

  const inp2 = "border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
  return (
    <div className="mt-8 border border-line bg-surface p-6" data-testid="premium-grant">
      <h2 className="flex items-center gap-2 font-serif-latin text-xl text-ink"><UserPlus className="h-5 w-5 text-saffron" /> Grant free access</h2>
      <p className="mt-1 text-sm text-ink-3">Give a user Panchangam Premium — choose <b>Single</b> or <b>Family</b> (5 members + 1 free consultation) — for a chosen number of days, no payment needed.</p>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="grant-tier">
        {[["single", "Single"], ["family", "Family (5 members)"]].map(([k, lbl]) => (
          <button key={k} type="button" onClick={() => setTier(k)} data-testid={`grant-tier-${k}`}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${tier === k ? "border-saffron bg-saffron/10 text-saffron" : "border-line text-ink-3 hover:text-ink"}`}>{lbl}</button>
        ))}
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_1fr_auto]">
        <input className={inp2} placeholder="user@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="grant-email" />
        <input className={inp2} type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} data-testid="grant-days" placeholder="Days" />
        <button onClick={grant} disabled={busy} data-testid="grant-submit" className="inline-flex items-center justify-center gap-1.5 bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Grant</button>
      </div>
      <label className="mt-2 flex items-center gap-2 text-xs text-ink-2">
        <input type="checkbox" checked={extend} onChange={(e) => setExtend(e.target.checked)} className="h-4 w-4 accent-[var(--saffron)]" /> Extend from current expiry if still active
      </label>

      <div className="mt-5" data-testid="premium-users">
        <p className="mb-2 overline-label text-ink-3">Premium members ({users.length})</p>
        {users.length === 0 ? <p className="text-sm text-ink-3">No premium members yet.</p> : (
          <div className="divide-y divide-line border-y border-line">
            {users.map((u, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`premium-user-${i}`}>
                <span className="text-ink">{u.email}{u.name ? ` · ${u.name}` : ""}</span>
                <span className="flex items-center gap-3 text-xs text-ink-3">
                  {u.tier === "family" && <span className="rounded-full bg-[rgba(176,141,87,0.15)] px-2 py-0.5 text-gold">family</span>}
                  <span className={`rounded-full px-2 py-0.5 ${u.source === "admin_grant" ? "bg-[rgba(176,141,87,0.15)] text-gold" : "bg-[rgba(199,80,0,0.1)] text-saffron"}`}>{u.source === "admin_grant" ? "granted" : u.status}</span>
                  {u.current_end && <span>until {String(u.current_end).slice(0, 10)}</span>}
                  <button onClick={() => revoke(u.email)} data-testid={`revoke-${i}`} className="text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
